import {
  alpha,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Calendar, Clock, Globe, Mic, MessageSquare, Target, Users, Video, X } from 'lucide-react';
import { formatDate } from '@utils/dateFormat';
import formatString from '@utils/formatString';
import { getStatusColor } from '@utils/colorUtils';

/**
 * Progress bar with gradient animation effect
 */
function AnimatedProgressBar({ value, max, showInfinity = false }) {
  const theme = useTheme();
  const displayMax = max || 0;
  const percentage = showInfinity ? Math.min(value * 5, 100) : displayMax > 0 ? (value / displayMax) * 100 : 0;
  const displayText = showInfinity ? '∞' : displayMax;

  const getProgressColor = () => {
    if (percentage >= 90) return theme.palette.error.main;
    if (percentage >= 70) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Responses
        </Typography>
        <Typography variant="caption" fontWeight={600}>
          {value} / {displayText}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha(getProgressColor(), 0.15),
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(percentage, 100)}%`,
            borderRadius: 4,
            background: `linear-gradient(90deg, ${getProgressColor()} 0%, ${alpha(getProgressColor(), 0.7)} 100%)`,
            transition: 'width 0.5s ease-out',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              animation: 'shimmer 2s infinite',
            },
            '@keyframes shimmer': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(100%)' },
            },
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * Info row component for consistent styling
 */
function InfoRow({ icon: Icon, label, value, valueColor }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={theme.palette.primary.main} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          color={valueColor || 'text.primary'}
          sx={{ wordBreak: 'break-word' }}
        >
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Study Info Dialog - Shows detailed study information in a quick preview
 */
export default function StudyInfoDialog({ open, onClose, study }) {
  const theme = useTheme();

  if (!study) return null;

  const isInterview = study.type === 'Interview';
  const isSurvey = study.type === 'Survey';

  // Extract study-specific fields
  const maxParticipants = study.max_participants || 0;
  const responses = study.response || study.total_responses || 0;
  const objective = study.objective || study.description || '';
  const duration = study.interview_duration || study.duration;
  const mode = study.interview_mode || study.mode;
  const languages = study.languages || [];
  const endDate = study.end_date || study.expires_at;
  const status = study.status;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box sx={{ flex: 1, pr: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              label={study.type}
              size="small"
              sx={{
                backgroundColor: isInterview
                  ? alpha(theme.palette.info.main, 0.1)
                  : alpha(theme.palette.success.main, 0.1),
                color: isInterview ? theme.palette.info.main : theme.palette.success.main,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getStatusColor(status),
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {formatString(status)}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {study.title}
          </Typography>
          {study.is_primary === false && (
            <Chip
              label="Secondary"
              size="small"
              variant="outlined"
              sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {/* Progress Section */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            mb: 2,
          }}
        >
          <AnimatedProgressBar
            value={responses}
            max={maxParticipants}
            showInfinity={isSurvey && maxParticipants !== 0}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Info Grid */}
        <Stack spacing={0.5}>
          {objective && <InfoRow icon={Target} label="Objective" value={objective} />}

          {isInterview && duration && (
            <InfoRow icon={Clock} label="Interview Duration" value={`${duration} minutes`} />
          )}

          {isInterview && mode && (
            <InfoRow
              icon={mode === 'voice' ? Mic : mode === 'video' ? Video : MessageSquare}
              label="Interview Mode"
              value={formatString(mode)}
            />
          )}

          <InfoRow
            icon={Users}
            label={isInterview ? 'Max Participants' : 'Participants'}
            value={isInterview ? maxParticipants || 'Unlimited' : `${responses} collected`}
          />

          {languages && languages.length > 0 && (
            <InfoRow icon={Globe} label="Languages" value={languages.join(', ')} />
          )}

          {endDate && (
            <InfoRow
              icon={Calendar}
              label="End Date"
              value={formatDate(endDate)}
              valueColor={new Date(endDate) < new Date() ? 'error.main' : undefined}
            />
          )}
        </Stack>

        {/* Quick Stats */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.grey[500], 0.05),
            display: 'flex',
            gap: 3,
          }}
        >
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              {responses}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Responses
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              {isInterview ? maxParticipants || '∞' : '∞'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isInterview ? 'Max Cap' : 'No Limit'}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              color={status === 'active' ? 'success.main' : 'text.secondary'}
            >
              {formatString(status)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
